import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formsApi } from '@/api';
import type { ClinicalRecord, FormResponseResult } from '@/types';

/** Read existing completed results; never recalculate scores in the browser. */
export default function PatientScaleResults({ patientId, records }: {
  patientId: string; records: ClinicalRecord[];
}) {
  const { t } = useTranslation();
  const [results, setResults] = useState<FormResponseResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    formsApi.patientResponses(patientId).then((items) => {
      if (active) setResults(items.filter((item) => item.status === 'COMPLETED')
        .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? '')));
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [patientId, retry]);

  return (
    <section className="card" data-cy="record-scale-results">
      <div className="card-header"><h2>{t('forms.title')} — {t('forms.history')}</h2></div>
      {loading ? <p>{t('common.loading')}</p> : error ? (
        <div role="alert" className="notice notice-error">
          {t('errors.network')} <button className="secondary is-pequeno" onClick={() => setRetry((n) => n + 1)}>{t('common.retry')}</button>
        </div>
      ) : results.length === 0 ? <p>{t('common.empty')}</p> : results.map((result) => {
        const score = result.scores;
        const record = records.find((item) => item.id === result.clinical_record_id);
        return (
          <details key={result.id} style={{ marginBottom: 'var(--espacio-4)' }}>
            <summary>
              <strong>{result.template_code}</strong> · {result.completed_at ? new Date(result.completed_at).toLocaleDateString('es-PY') : '—'}
              {score && <> · {score.total_score}/{score.max_score} · {score.severity}</>}
              {score?.requires_immediate_review && <span className="badge badge-critical" style={{ marginLeft: '0.5rem' }}>{t('forms.criticalAlert')}</span>}
            </summary>
            <p className="t-pequeno t-suave">{t('forms.linkToRecord')}: {record
              ? `${new Date(record.session_date).toLocaleDateString('es-PY')} — ${t(`record.types.${record.record_type}`)}`
              : t('patientHistory.noLinkedRecord')}</p>
            {score && <>
              <p><strong>{t('forms.interpretation')}:</strong> {score.interpretation}</p>
              <p><strong>{t('forms.recommendedAction')}:</strong> {score.recommended_action}</p>
              {!!score.alerts?.length && <div className="notice notice-warning">
                <ul>{score.alerts.map((alert) => <li key={alert.scoring_key}>
                  {alert.message} {alert.action && <em>{t('forms.alertAction')}: {alert.action}</em>}
                </li>)}</ul>
                <p>{t('forms.criticalAlertHint')}</p>
              </div>}
            </>}
          </details>
        );
      })}
    </section>
  );
}
